import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, IconButton, Tooltip, FormControl, Select, MenuItem, TextField, Alert, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CustomButton from '../../../../components/buttons/CustomButton';
import { buildTimeOptions, formatDayLabel } from '../utils';
import styles from '../Recovery.module.css';

const PAPER_SX = { width: { xs: '100%', sm: 480 }, boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.08)' };

const AI_COLOR = 'var(--color-blue-main, #5A80FB)';

/**
 * Drawer "Chamar": intervalo, pessoa, horário para oferecer depois da resposta
 * (escolhido dentro do intervalo, fora da mensagem), mensagem editável e o
 * aviso de que o convite não cita horário. O texto
 * começa com o modelo e é trocado pelo convite da IA quando ele chega
 * (`compose`: idle | loading | ai | template), a não ser que a pessoa já
 * tenha editado. O envio de verdade é do pai (revalidação e fila no backend).
 */
function ContactDrawer({ open, onClose, interval, candidate, stepMinutes, compose, onRecompose, sending, error, onSend }) {
  const minutes = candidate?.suggestedProcedure?.minutes || stepMinutes;
  const timeOptions = useMemo(
    () => buildTimeOptions(interval, minutes, stepMinutes, candidate?.suggestedTime),
    [interval, minutes, stepMinutes, candidate]
  );

  const [time, setTime] = useState('');
  const [message, setMessage] = useState('');
  // Origem do texto atual: template | ai; vira *_edited quando a pessoa mexe
  const [source, setSource] = useState('template');
  const [edited, setEdited] = useState(false);

  // Ao abrir para outra pessoa, volta ao horário e à mensagem sugeridos pela API
  useEffect(() => {
    if (!open || !candidate) return;
    const suggested = candidate.suggestedTime && timeOptions.includes(candidate.suggestedTime)
      ? candidate.suggestedTime
      : (timeOptions[0] || interval?.start || '');
    setTime(suggested);
    setMessage(candidate.message || '');
    setSource('template');
    setEdited(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidate?.patientId]);

  // O convite da IA chegou: substitui o modelo, a não ser que a pessoa já tenha editado
  const composeStatus = compose?.status || 'idle';
  useEffect(() => {
    if (!open || composeStatus !== 'ai' || !compose?.message || edited) return;
    setMessage(compose.message);
    setSource('ai');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeStatus, compose?.message]);

  // Troca de horário: se a mensagem citar o horário (modelo próprio da empresa), ele acompanha
  function handleTimeChange(next) {
    setMessage((current) => (time && current.includes(time) ? current.replaceAll(time, next) : current));
    setTime(next);
  }

  function handleMessageChange(value) {
    setMessage(value);
    setEdited(true);
  }

  // "Escrever de novo": pede outro convite para o horário atual e aceita o resultado mesmo após edição
  function handleRecompose() {
    if (!onRecompose || !time) return;
    setEdited(false);
    onRecompose(time, minutes);
  }

  const composing = composeStatus === 'loading';
  const messageSource = `${source}${edited ? '_edited' : ''}`;
  const canSend = Boolean(time) && message.trim().length > 0 && !sending && !composing;

  // A mensagem vai para o responsável (bebê, criança, adolescente)? O cabeçalho diz para quem.
  const recipient = candidate?.recipient;
  const toResponsible = recipient?.kind === 'responsible';
  const recipientLine = toResponsible
    ? `Para ${recipient.name || 'o responsável'}${recipient.relationship ? ` (${recipient.relationship})` : ''}`
    : '';
  const subtitle = [recipientLine, candidate?.phone].filter(Boolean).join(' · ');
  const withChat = Boolean(candidate?.chat?.summary);

  let hint = null;
  if (composing) {
    hint = withChat ? 'Escrevendo o convite a partir do histórico e da última conversa...' : 'Escrevendo o convite a partir do histórico...';
  } else if (source === 'ai') {
    if (edited) {
      hint = 'Convite da IA, revisado por você.';
    } else {
      hint = withChat
        ? 'Convite escrito pela IA a partir dos motivos e do tom da última conversa. Revise antes de enviar.'
        : 'Convite escrito pela IA a partir dos motivos. Revise antes de enviar.';
    }
  } else if (composeStatus === 'template') {
    hint = 'A IA não respondeu agora; este é o modelo padrão.';
  }

  return (
    <Drawer anchor="right" open={open} onClose={sending ? undefined : onClose} PaperProps={{ sx: PAPER_SX }}>
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <div>
            <span className={styles.drawerKicker}>Chamar pelo WhatsApp</span>
            <h3 className={styles.drawerTitle}>{candidate?.name}</h3>
            {subtitle && <p className={styles.drawerSubtitle}>{subtitle}</p>}
          </div>
          <Tooltip title="Fechar" arrow>
            <span>
              <IconButton onClick={onClose} size="small" aria-label="Fechar" disabled={sending}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <div className={styles.drawerBody}>
          {interval && (
            <div className={styles.summaryBox}>
              <span className={styles.summaryStrong}>
                {formatDayLabel(interval.date)}, {interval.start} às {interval.end}
              </span>
              <span className={styles.summaryLine}>
                {[interval.professionalName, `${interval.minutes} min livres`].filter(Boolean).join(' · ')}
              </span>
              {candidate?.suggestedProcedure && (
                <span className={styles.summaryLine}>
                  {candidate.suggestedProcedure.label} · {candidate.suggestedProcedure.minutes} min
                </span>
              )}
            </div>
          )}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Horário para oferecer</span>
            <FormControl size="small">
              <Select
                value={timeOptions.includes(time) ? time : ''}
                onChange={(e) => handleTimeChange(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.86rem', maxWidth: 180 }}
              >
                {timeOptions.length === 0 && <MenuItem value="">Sem horário que caiba</MenuItem>}
                {timeOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </Select>
            </FormControl>
            <span className={styles.fieldHint}>Atendimento de {minutes} min. Não vai na mensagem: fica guardado para você oferecer quando a pessoa responder.</span>
          </div>

          <div className={styles.field}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className={styles.fieldLabel}>Mensagem</span>
              {onRecompose && (
                <Tooltip title={composing ? 'Escrevendo...' : 'Escrever de novo com IA'} arrow>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Escrever de novo com IA"
                      onClick={handleRecompose}
                      disabled={composing || sending || !time}
                      style={{ color: AI_COLOR }}
                    >
                      {composing ? <CircularProgress size={16} /> : <AutoAwesomeIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </div>
            <TextField
              multiline
              rows={7}
              value={message}
              onChange={(e) => handleMessageChange(e.target.value)}
              size="small"
              fullWidth
              disabled={composing}
              placeholder={composing ? 'Escrevendo o convite...' : ''}
              inputProps={{ style: { fontSize: '0.86rem', lineHeight: 1.5 } }}
            />
            {hint && (
              <span className={styles.fieldHint} style={source === 'ai' && !composing ? { color: AI_COLOR } : undefined}>
                {hint}
              </span>
            )}
          </div>

          <div className={styles.notice}>
            <InfoOutlinedIcon style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />
            <span>O convite chama para retomar, sem citar horário. Quando a pessoa responder, ofereça o horário e agende normalmente.</span>
          </div>

          {error && <Alert severity="error" sx={{ mt: 2, fontSize: '0.82rem' }}>{error}</Alert>}
        </div>

        <div className={styles.drawerFooter}>
          <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={onClose} enabled={!sending} />
          <CustomButton
            variant="contained"
            style="primary"
            label="Enviar"
            icon={<SendOutlinedIcon />}
            loading={sending}
            enabled={canSend}
            onClick={() => onSend({ time, minutes, message: message.trim(), messageSource })}
          />
        </div>
      </div>
    </Drawer>
  );
}

export default ContactDrawer;
